/**
 * Optical logo balancing for every logo wall / matchup on the public site.
 *
 * Logos arrive in every shape (wide wordmarks, square badges, round crests) and
 * often with a baked-in white canvas. Dropped into identical boxes with
 * `object-fit: contain`, a wide wordmark fills the width while a square badge
 * looks tiny, so rows read as uneven. This scales each logo so its painted area
 * is roughly constant — the technique league / sponsor walls use — based only
 * on the image's natural aspect ratio (no pixel access needed, so it works with
 * Storage URLs that don't send CORS headers).
 *
 * Install once at startup. It listens for image `load` events in the capture
 * phase, so it covers images rendered by any page, including lazy ones.
 *
 * - Tile logos (light tile behind the image): sets `--logo-s` (a scale factor);
 *   CSS applies it as a transform.
 * - Circle logos (the image itself is the white disc): sets `--logo-pad`
 *   (a % padding), shrinking the artwork inside a constant-size disc.
 */

const TILE_SELECTOR = [
  '.home-sponsor__img',
  '.sp-logo-tile img',
  '.sp-logo-item img',
].join(',');

const CIRCLE_SELECTOR = [
  '.vb-game__logo',
  '.home-matchup__logo-img',
  '.home-ticket__logo',
].join(',');

// Target share of the box the artwork should cover (by area).
const TILE_TARGET = 0.52;
const CIRCLE_TARGET = 0.5;

const coverage = (imageRatio: number, boxRatio: number) =>
  Math.min(boxRatio / imageRatio, imageRatio / boxRatio);

const balance = (img: HTMLImageElement) => {
  const { naturalWidth: nw, naturalHeight: nh } = img;
  if (!nw || !nh) return;
  const r = nw / nh;

  if (img.matches(TILE_SELECTOR)) {
    const box = img.parentElement;
    const bw = box?.clientWidth || img.clientWidth;
    const bh = box?.clientHeight || img.clientHeight;
    if (!bw || !bh) return;
    const s = Math.min(1, Math.sqrt(TILE_TARGET / coverage(r, bw / bh)));
    img.style.setProperty('--logo-s', s.toFixed(3));
    img.dataset.logoBalanced = 'tile';
  } else if (img.matches(CIRCLE_SELECTOR)) {
    // Inside a disc the usable area is the inscribed square; wider artwork
    // needs less padding, squarer artwork more. Clamp to sensible bounds.
    const s = Math.min(1, Math.sqrt(CIRCLE_TARGET / coverage(r, 1)));
    // Padding as a fraction of the disc's own size (CSS % padding would
    // resolve against the parent's width, so compute pixels here).
    const frac = 0.09 + (1 - s) * 0.22; // 9%–~20% each side
    const size = img.clientWidth || img.offsetWidth;
    if (!size) return;
    img.style.setProperty('--logo-pad', `${(size * frac).toFixed(1)}px`);
    img.dataset.logoBalanced = 'circle';
  }
};

let installed = false;

export const installLogoBalance = () => {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener(
    'load',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLImageElement) balance(target);
    },
    true
  );

  // Images that finished loading before React attached them (cache hits) or
  // before this ran: balance them once they're in the DOM.
  const sweep = (root: ParentNode) => {
    root.querySelectorAll<HTMLImageElement>(`${TILE_SELECTOR},${CIRCLE_SELECTOR}`).forEach((img) => {
      if (img.complete && !img.dataset.logoBalanced) balance(img);
    });
  };
  // Disc sizes change at breakpoints; re-balance on resize (debounced).
  let resizeTimer: number | undefined;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll<HTMLImageElement>(CIRCLE_SELECTOR).forEach((img) => {
        if (img.complete) balance(img);
      });
    }, 150);
  });

  if (typeof MutationObserver !== 'undefined') {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        sweep(document);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
};
